import { MachineFile } from '@/screencheck_portal_api/src'
import _ from 'lodash'

export function emptyMachineFile () {
  return MachineFile.constructFromObject({}, {
    uuid: null,
    version: 3,
    timestamp: null,
    machineId: null,
    revision: null,
    derivedFromMachineId: null,
    derivedFromRevision: null,
    static: {
      machineSerialNumber: null,
      machineType: null,
      dealerName: null,
      customerEquipmentCode: null,
      customerName: null,
      siteName: null,
      application: null,
      decksAmount: null,
      driveType: null,
      drivePosition: null,
      shaftLinesAmount: null,
      excitersAmount: null,
      mechanismType: null,
      exciterTypes: [],
      lubrication: null,
      motionType: null
    },
    fixed: {
      weight: null,
      weightMethod: null,
      process: {
        materialType: null,
        dryWet: null,
        topFeedSize: null,
        feedTonnage: null,
        dropHeightFeedMaterial: null,
        feedSizeMin: null,
        feedSizeMax: null,
        constant: null,
        surging: null
      },
      counterweights: [],
      media: {
        topDeck: {
          type: null,
          competitorName: null,
          typeSpecificity: null,
          material: null,
          openingSize: null,
          openingShape: null
        },
        upperMidDeck: {
          type: null,
          competitorName: null,
          typeSpecificity: null,
          material: null,
          openingSize: null,
          openingShape: null
        },
        lowerMidDeck: {
          type: null,
          competitorName: null,
          typeSpecificity: null,
          material: null,
          openingSize: null,
          openingShape: null
        },
        bottomDeck: {
          type: null,
          competitorName: null,
          typeSpecificity: null,
          material: null,
          openingSize: null,
          openingShape: null
        }
      },
      isolationFramePresent: null,
      screenSuspensionType: null,
      screenSuspensionFeedDischarge: {
        innerPartNumber: null,
        outerPartNumber: null,
        feedLeftInner: null,
        feedLeftOuter: null,
        feedRightInner: null,
        feedRightOuter: null,
        dischargeLeftInner: null,
        dischargeLeftOuter: null,
        dischargeRightInner: null,
        dischargeRightOuter: null,
        feedPredictedCompressedSpringHeight: null,
        dischargePredictedCompressedSpringHeight: null
      },
      screenSidewaysSuspensionType: null,
      isolationFrameMountedOnSuspensions: null,
      isolationFrameCorrectlySpecified: null,
      isolationFrameSuspensionType: null,
      isolationFrameFeedDischarge: {
        innerPartNumber: null,
        outerPartNumber: null,
        feedLeftInner: null,
        feedLeftOuter: null,
        feedRightInner: null,
        feedRightOuter: null,
        dischargeLeftInner: null,
        dischargeLeftOuter: null,
        dischargeRightInner: null,
        dischargeRightOuter: null,
        feedPredictedCompressedSpringHeight: null,
        dischargePredictedCompressedSpringHeight: null
      },
      lubricationSpec: null,
      greasingPowerUnit: null,
      motor: {
        motorFrequency: null,
        expectedSpeed: null,
        rotationDirection: null,
        vsdvfdInstalled: null,
        electronicBrakeInstalled: null,
        motorSheaveDiameterAsSpecified: null,
        drivenSheaveDiameterAsSpecified: null,
        motorA: {
          type: null,
          manufacturer: null,
          serial: null
        },
        motorB: {
          type: null,
          manufacturer: null,
          serial: null
        },
        exciterSerials: [],
        exciterLocations: []
      },
      gaDrawingNumberRevision: null,
      instructionManualNumberRevision: null,
      sparePartBookNumberRevision: null
    }
  })
}

// Returns a plain JavaScript object structure intended for reactive editing in Vue
// Important: do NOT use MachineFile.constructFromObject here, as it creates class
// instances that Vue cannot fully observe, which prevents UI updates (e.g. button
// variants) after clicks in the merger UI.
export function machineFileStructure () {
  return {
    static: {},
    fixed: {
      process: {},
      media: {
        topDeck: {},
        upperMidDeck: {},
        lowerMidDeck: {},
        bottomDeck: {},
      },
      screenSuspensionFeedDischarge: {},
      isolationFrameFeedDischarge: {},
      motor: {
        motorA: {},
        motorB: {},
      },
    },
  }
}

export function defaultMachineFile (serial, machineType, customerName) {
  const machineFile = emptyMachineFile()

  machineFile.static.machineSerialNumber = serial
  machineFile.static.machineType = machineType
  machineFile.static.customerName = customerName

  return machineFile
}

// Returns the machine file data from the API merged into the default machine file, as plain objects for reactive editing
// Important: the API client returns class instances (e.g. MachineFileStatic). _.merge does not merge into the defaults
// but replaces them with such an instance, and its missing fields exist only on the prototype, where Vue cannot observe
// them (e.g. the exciter type fields did not follow a change of the number of shaft lines).
export function machineFileWithDefaults (serial, data) {
  return _.merge(defaultMachineFile(serial), JSON.parse(JSON.stringify(data)))
}

// Rules for the drive and mechanism fields, the same as MachineFileRules in the ScreenCheck App.
export const DRIVE_TYPE_UNBALANCED_MOTORS = 'Unbalanced Motors'
export const UNBALANCED_MOTORS_EXCITERS = 2
export const MECHANISM_TYPE_BLZ = 'BLz'
// Name of the BLz mechanism type up to App 2.0.1, still found in existing machine files.
export const LEGACY_MECHANISM_TYPE_BLZ = 'BLz / Unbalanced Motor'

export function isUnbalancedMotors (driveType) {
  return driveType?.toLowerCase() === DRIVE_TYPE_UNBALANCED_MOTORS.toLowerCase()
}

export function normalizeMechanismType (mechanismType) {
  return mechanismType === LEGACY_MECHANISM_TYPE_BLZ ? MECHANISM_TYPE_BLZ : mechanismType
}

// Unbalanced motors have no shaft lines, mechanism type, exciter types or counterweights (hidden in the editor), but two exciters.
export function applyUnbalancedMotors (machineFile) {
  machineFile.static.shaftLinesAmount = null
  machineFile.static.excitersAmount = UNBALANCED_MOTORS_EXCITERS
  machineFile.static.mechanismType = null
  machineFile.static.exciterTypes = []
  if (machineFile.fixed) {
    machineFile.fixed.counterweights = []
  }
}
